// ABOUTME: Family-wide "today's behavior summary" card — hearts on track, stars, triangles, storms
// ABOUTME: All four tiles are raw sums/counts across the family, matching the mockup's simple-count style

import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { EnhancedChildDashboard } from '../../types/api';
import { BilingualLabel } from '../BilingualLabel';
import { BilingualTerm } from '../../utils/bilingualGlossary';
import { StarConstellationIcon } from '../StarConstellationIcon';
import { TriangleCloudIcon } from '../TriangleCloudIcon';

export interface TodaySeaCardProps {
  familyChildren: EnhancedChildDashboard[];
}

function StatTile({ testId, icon, value, term, english }: { testId: string; icon: React.ReactNode; value: React.ReactNode; term: BilingualTerm; english: string }) {
  return (
    <Box sx={{ bgcolor: 'background.default', border: '1px solid', borderColor: 'warning.dark', borderRadius: '12px', p: 1.5, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h6" data-testid={testId} sx={{ fontWeight: 800 }}>{value}</Typography>
      <Typography variant="caption" component="div">
        <BilingualLabel term={term} english={english} />
      </Typography>
    </Box>
  );
}

export const TodaySeaCard: React.FC<TodaySeaCardProps> = ({ familyChildren }) => {
  const heartsOnTrack = familyChildren.filter(c => c.onTrackForHeartToday).length;
  const stars = familyChildren.reduce((sum, c) => sum + c.todaysBehaviorCounts.starCount, 0);
  const triangles = familyChildren.reduce((sum, c) => sum + c.todaysBehaviorCounts.triangleCount, 0);
  const storms = familyChildren.reduce((sum, c) => sum + c.todaysBehaviorCounts.squareCount, 0);

  return (
    <Paper sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'warning.dark', borderRadius: '18px', p: 2.5 }} elevation={0}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        <BilingualLabel term="todaySea" english="Today at sea" />
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <StatTile testId="today-sea-hearts" icon={<FavoriteIcon sx={{ color: 'error.main' }} />} value={`${heartsOnTrack} of ${familyChildren.length}`} term="heart" english="Hearts on track" />
        <StatTile testId="today-sea-stars" icon={<StarConstellationIcon size={32} />} value={stars} term="star" english="Stars logged" />
        <StatTile testId="today-sea-triangles" icon={<TriangleCloudIcon size={32} />} value={triangles} term="triangle" english="Triangles" />
        <StatTile testId="today-sea-storms" icon={<Box component="img" src="/nalu/storm-128.png" alt="Storm" sx={{ width: 32, height: 32 }} />} value={storms} term="storm" english="Storms" />
      </Box>
    </Paper>
  );
};
